import { useTranslation } from "@sagot/i18n";
import { Badge, Button, FileDropzone, Icon, Textarea } from "@sagot/ui";
import { useId } from "react";
import { downloadFile } from "../shared/download";
import type { ImportOut } from "../shared/types";
import { CSV_MAX_BYTES, CSV_TEMPLATE } from "./eval-model";

/** Header columns + data row count of a CSV (quotes respected for newlines). */
export function csvShape(csv: string): { rows: number; columns: string[] } {
  const lines: string[] = [];
  let current = "";
  let quoted = false;
  for (const char of csv) {
    if (char === '"') quoted = !quoted;
    if (char === "\n" && !quoted) {
      lines.push(current);
      current = "";
    } else current += char;
  }
  if (current.trim()) lines.push(current);
  const rows = lines.filter((l) => l.trim());
  const header = rows[0] ?? "";
  return { rows: Math.max(rows.length - 1, 0), columns: header ? header.split(",").map((c) => c.trim().replace(/^"|"$/g, "")) : [] };
}

export function ImportSummary({ result }: { result: ImportOut }) {
  const { t } = useTranslation();
  const ok = result.valid > 0 && result.errors.length === 0;
  return (
    <div className="rounded-md border border-line" role="status">
      <div className="flex items-center gap-2.5 border-b border-line px-3 py-2.5">
        <Icon name="file-spreadsheet" className="size-5 text-primary-ink" />
        <div className="min-w-0 flex-1 text-xs text-fg-subtle">{t("build.evaluations.csv.summary", { rows: result.rows, columns: result.columns.length })}</div>
        <Badge tone={ok ? "success" : "warning"}>{ok ? t("build.evaluations.csv.valid") : t("build.evaluations.csv.invalid")}</Badge>
      </div>
      {result.errors.length > 0 ? (
        <ul className="list-disc space-y-0.5 px-7 py-2 text-xs text-fg-muted" aria-label={t("build.evaluations.csv.errors")}>
          {result.errors.slice(0, 8).map((e) => <li key={e}>{e}</li>)}
        </ul>
      ) : null}
    </div>
  );
}

/** CSV source: file (≤ 512 KB) or pasted text, plus the downloadable template. */
export function CsvField({ value, onChange, disabled }: { value: string; onChange: (csv: string) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const pasteId = useId();
  const shape = value.trim() ? csvShape(value) : null;
  return (
    <div className="space-y-3">
      <span className="label">{t("build.evaluations.csv.label")}</span>
      <FileDropzone
        accept={[".csv"]}
        maxBytes={CSV_MAX_BYTES}
        multiple={false}
        disabled={disabled}
        labels={{ title: t("build.evaluations.csv.dropzone.title"), hint: `${t("build.evaluations.csv.columns")} · ${t("build.evaluations.csv.dropzone.hint")}` }}
        onFiles={(files) => { const file = files[0]; if (file) void file.text().then(onChange); }}
      />
      <label htmlFor={pasteId} className="label">{t("build.evaluations.csv.paste")}</label>
      <Textarea id={pasteId} rows={4} className="font-mono text-xs" placeholder={t("build.evaluations.csv.columns")} value={value} disabled={disabled} onChange={(e) => { onChange(e.target.value); }} />
      {shape ? <p className="text-xs text-fg-subtle" role="status">{t("build.evaluations.csv.summary", { rows: shape.rows, columns: shape.columns.length })}</p> : null}
      <Button type="button" variant="ghost" size="sm" onClick={() => { downloadFile("test-set-template.csv", CSV_TEMPLATE, "text/csv;charset=utf-8"); }}>
        <Icon name="download" />
        {t("build.evaluations.csv.template")}
      </Button>
    </div>
  );
}
